import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ElementType, KeyboardEvent, ReactNode } from "react";
import { CHARGE, INK, NEUTRAL, OVERTIME, SURFACE } from "../../../theme/tokens";
import { PtoCornerFold } from "./PtoCornerFold";

/** Overtime state carried by an assignment chip. */
export type ChipOtState = "approaching" | "over";

/**
 * Cross-grid linked-hover state for a chip:
 *  - `"none"` — no hover link active (neutral, the default).
 *  - `"match"` — this chip is one of the highlighted set (e.g. the same person the
 *    user is hovering elsewhere): brand-tinted outline + lift, brought forward.
 *  - `"dim"` — a hover link IS active but this chip is not in the set: de-emphasized
 *    so the matches pop. Letting the caller drive this keeps NameChip generic — it
 *    owns only the presentation of each state, never which chips match.
 */
export type ChipHighlightState = "none" | "match" | "dim";

export type NameChipProps = {
	/** Display name (already formatted, e.g. "Bernice T."). */
	name: string;
	/** Optional trailing role code (e.g. "RN"). */
	role?: string;
	/**
	 * Charge marker — renders a small categorical "C" badge. Pass the already
	 * translated single-letter label so the chip stays i18n-driven by its caller.
	 */
	charge?: boolean;
	/** Charge badge label (already translated, e.g. "C"). Required when `charge`. */
	chargeLabel?: string;
	/** Overtime badge state; `undefined` = no badge. */
	ot?: ChipOtState;
	/** OT badge label (already translated, e.g. "OT"). Required when `ot` is set. */
	otLabel?: string;
	/**
	 * On PTO for this day — strikes the name, dims the chip, and renders the
	 * corner-fold marker (the app-wide PTO idiom). A PTO chip is not draggable.
	 */
	onPto?: boolean;
	/** Show a grab handle on the left (drag affordance). */
	showDragHandle?: boolean;
	/** Visually mark the chip as the source being moved (lifted/ghosted). */
	isDragging?: boolean;
	/**
	 * Cross-grid linked-hover state (see `ChipHighlightState`). Defaults to
	 * `"none"`. Drives the chip's own `data-highlight` attribute; the look is CSS —
	 * so a caller can flip a chip between states by mutating that attribute
	 * imperatively (no React re-render) rather than re-rendering with a new prop.
	 * The caller decides which chips are a `"match"` vs `"dim"`; the chip only
	 * renders the look. Honors reduced-motion (color/opacity only).
	 */
	highlightState?: ChipHighlightState;
	/**
	 * Identity for CSS-driven linked hover: chips sharing this id form one group.
	 * Stamped as `data-linked-hover-id` so a caller can, on hover, mark the group
	 * (`data-highlight="match"`) and dim the rest — via a stable ancestor toggling
	 * `data-linked-hover` — WITHOUT going through React state. See the v3
	 * Master-Schedule grids' `useLinkedHover`.
	 */
	linkedHoverGroupId?: string | number;
	/** Fires on pointer enter (the chip's identity) and leave (`null`) — drives linked hover. */
	onHoverChange?: (hovered: boolean) => void;
	/**
	 * Root element. Defaults to a `div`; pass `"span"` when the chip is rendered inside phrasing-only
	 * content — a `<button>` row in a picker list, a `<label>` — where a `div` would be invalid HTML.
	 * The chip is already `inline-flex`, so this changes nothing visually.
	 */
	component?: ElementType;
	/** `aria-label` override; falls back to `name`. */
	ariaLabel?: string;
	onClick?: () => void;
	/** Slot for caller-owned end content (e.g. a remove button). */
	endContent?: ReactNode;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * Compact assigned-person chip for dense coverage / staffing grids: a name with
 * optional role, a categorical charge badge, an overtime badge, and the
 * app-wide PTO corner-fold + strikethrough idiom. Generic over any
 * "who is assigned to this slot" surface — the chip renders indicators it's
 * told to render; it computes nothing. Drag wiring (dnd-kit, etc.) is the
 * caller's responsibility — this only renders the affordance (`showDragHandle`)
 * and the dragging state.
 */
export const NameChip = ({
	name,
	role,
	charge,
	chargeLabel,
	ot,
	otLabel,
	onPto,
	showDragHandle,
	isDragging,
	highlightState = "none",
	linkedHoverGroupId,
	onHoverChange,
	component = "div",
	ariaLabel,
	onClick,
	endContent,
	sx,
	dataTestId,
}: NameChipProps) => (
	<Box
		component={component}
		role={onClick ? "button" : undefined}
		tabIndex={onClick ? 0 : undefined}
		aria-label={ariaLabel ?? name}
		onClick={onClick}
		// The event is annotated because `component` is an `ElementType`, which loosens MUI's own inference.
		onKeyDown={
			onClick
				? (e: KeyboardEvent<HTMLElement>) => {
						if (e.key !== "Enter" && e.key !== " ") return;
						e.preventDefault();
						onClick();
					}
				: undefined
		}
		onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
		onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
		data-testid={dataTestId}
		data-highlight={highlightState}
		data-linked-hover-id={linkedHoverGroupId}
		data-pto={onPto ? "" : undefined}
		sx={[
			(theme) => {
				// PTO / drag opacity are fixed by props; only the match/dim look flips at
				// hover time. So the base sx holds the none-state look, and the match/dim
				// looks are attribute-keyed CSS — mutating `data-highlight` (or the ancestor
				// `data-linked-hover` flag) restyles the chip with NO React render.
				const dimOpacity = onPto ? 0.72 : isDragging ? 0.5 : 0.38;
				return {
					position: "relative",
					display: "inline-flex",
					alignItems: "center",
					gap: 0.375,
					minWidth: 0,
					maxWidth: "100%",
					px: 0.625,
					py: "2px",
					borderRadius: 0.75,
					backgroundColor: onPto ? NEUTRAL[100] : SURFACE,
					border: `1px solid ${NEUTRAL[200]}`,
					boxShadow: isDragging ? `0 2px 6px ${NEUTRAL[400]}` : "none",
					opacity: onPto ? 0.72 : isDragging ? 0.5 : 1,
					cursor: onClick ? "pointer" : "default",
					overflow: "hidden",
					transition: "opacity 120ms cubic-bezier(0.23, 1, 0.32, 1), border-color 120ms, box-shadow 120ms, background-color 120ms",
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					// A match gets a brand-tinted ring + wash (PTO keeps its muted fill).
					'&[data-highlight="match"]': {
						backgroundColor: onPto ? NEUTRAL[100] : alpha(theme.palette.primary.main, 0.08),
						borderColor: theme.palette.primary.main,
						boxShadow: isDragging ? `0 2px 6px ${NEUTRAL[400]}` : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
					},
					// A statically-dimmed chip recedes (PTO keeps its own softer opacity).
					'&[data-highlight="dim"]:not([data-pto])': { opacity: dimOpacity },
					// CSS linked hover: while an ancestor is hovering a group, every non-match,
					// non-PTO chip recedes — driven purely by the ancestor flag, no re-render.
					'[data-linked-hover] &:not([data-highlight="match"]):not([data-pto])': { opacity: dimOpacity },
				};
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{showDragHandle && !onPto && (
			<DragIndicatorIcon
				sx={{ fontSize: 13, color: NEUTRAL[400], flexShrink: 0, cursor: "grab", "&:active": { cursor: "grabbing" } }}
				aria-hidden
				data-drag-handle
			/>
		)}
		{charge && chargeLabel && (
			<Box
				component="span"
				aria-hidden
				sx={{
					flexShrink: 0,
					fontSize: 8.5,
					fontWeight: 800,
					lineHeight: 1.4,
					letterSpacing: "0.02em",
					color: CHARGE.INK,
					backgroundColor: CHARGE.BG,
					px: 0.375,
					borderRadius: 0.5,
				}}
			>
				{chargeLabel}
			</Box>
		)}
		<Box
			component="span"
			sx={{
				minWidth: 0,
				fontSize: 11,
				fontWeight: 600,
				lineHeight: 1.3,
				color: onPto ? INK.MUTED : INK.PRIMARY,
				textDecoration: onPto ? "line-through" : "none",
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
		>
			{name}
		</Box>
		{role && (
			<Box component="span" sx={{ flexShrink: 0, fontSize: 9, fontWeight: 700, color: INK.MUTED }}>
				{role}
			</Box>
		)}
		{ot && otLabel && (
			<Box
				component="span"
				aria-hidden
				sx={{
					flexShrink: 0,
					fontSize: 8.5,
					fontWeight: 800,
					lineHeight: 1.4,
					letterSpacing: "0.02em",
					color: OVERTIME.INK,
					backgroundColor: OVERTIME.BG,
					px: 0.375,
					borderRadius: 0.5,
					// Approaching-OT is the softer signal; over-OT owns the full weight.
					opacity: ot === "approaching" ? 0.72 : 1,
				}}
			>
				{otLabel}
			</Box>
		)}
		{endContent}
		{onPto && <PtoCornerFold size={8} />}
	</Box>
);
